import React from 'react';
import { Dialog } from '@headlessui/react';
import { CheckCircle2, LogOut, Trash2, X } from 'lucide-react';
import { useTheme } from './ThemeProvider';

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Are you sure you want to delete?',
    message = '',
    confirmText = 'Yes',
    cancelText = 'No',
    type = 'default', // 'logout', 'delete', or 'default'
    children = null,
    confirmDisabled = false,
    useThemeColor = false,
    zIndex = 50,
}) => {
    const { currentTheme } = useTheme();

    // Style variants based on type
    const getVariant = () => {
        switch (type) {
            case 'delete':
                return {
                    Icon: Trash2,
                    icon: 'bg-rose-100 text-rose-600',
                    confirm: 'bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-200',
                    cancel: 'bg-slate-100 text-slate-700 hover:bg-slate-200 focus:ring-slate-200',
                    accent: 'from-rose-50 via-white to-white',
                };
            case 'logout':
                return {
                    Icon: LogOut,
                    icon: 'text-white',
                    confirm: 'text-white focus:ring-slate-300',
                    cancel: 'bg-slate-100 text-slate-700 hover:bg-slate-200 focus:ring-slate-200',
                    accent: 'from-slate-100 via-white to-white',
                    themed: true,
                };
            default:
                return {
                    Icon: CheckCircle2,
                    icon: 'bg-blue-100 text-blue-700',
                    confirm: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-200',
                    cancel: 'bg-slate-100 text-slate-700 hover:bg-slate-200 focus:ring-slate-200',
                    accent: 'from-blue-50 via-white to-white',
                };
        }
    };

    const { Icon, icon, confirm, cancel, accent, themed } = getVariant();
    const shouldUseThemeColor = themed || useThemeColor;

    // Ref kept for keyboard accessibility without stealing focus from form fields.
    const confirmBtnRef = React.useRef(null);

    // Handle Enter/Return key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Return') {
            if (confirmDisabled) return;
            onConfirm();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0"
            style={{ zIndex }}
            data-modal-back="true"
        >
            <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel
                    className={`relative w-full max-w-md overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.22)]`}
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                >
                    <div
                        className={`absolute inset-x-0 top-0 h-28 bg-gradient-to-br ${accent}`}
                        style={useThemeColor ? {
                            background: `linear-gradient(135deg, color-mix(in srgb, ${currentTheme.color} 16%, white), white 72%)`,
                        } : undefined}
                    />
                    <button
                        type="button"
                        data-modal-close
                        onClick={onClose}
                        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                        aria-label="Close confirmation"
                    >
                        <X className="h-4.5 w-4.5" />
                    </button>

                    <div className="relative p-6 text-center sm:p-7">
                        <div
                            className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl shadow-sm ${icon}`}
                            style={shouldUseThemeColor ? {
                                background: `color-mix(in srgb, ${currentTheme.color} 16%, white)`,
                                color: currentTheme.color,
                            } : undefined}
                        >
                            <Icon className="h-6 w-6" />
                        </div>

                        <Dialog.Title className="mt-5 text-2xl font-semibold tracking-tight text-slate-950">
                            {title}
                        </Dialog.Title>

                        {message && (
                            <Dialog.Description className="mx-auto mt-3 max-w-xs text-sm leading-6 text-slate-500">
                                {message}
                            </Dialog.Description>
                        )}

                        {children && (
                            <div className="mt-5 text-left">
                                {children}
                            </div>
                        )}

                        <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
                        <button
                            onClick={onClose}
                            className={`rounded-2xl px-5 py-3 text-sm font-semibold transition focus:outline-none focus:ring-4 sm:min-w-[112px] ${cancel}`}
                        >
                            {cancelText}
                        </button>
                        <button
                            ref={confirmBtnRef}
                            onClick={() => {
                                if (!confirmDisabled) onConfirm();
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === 'Return') {
                                    if (confirmDisabled) return;
                                    onConfirm();
                                }
                            }}
                            disabled={confirmDisabled}
                            className={`rounded-2xl px-5 py-3 text-sm font-semibold shadow-[0_14px_28px_rgba(15,23,42,0.16)] transition focus:outline-none focus:ring-4 disabled:cursor-not-allowed disabled:opacity-55 sm:min-w-[112px] ${confirm}`}
                            style={shouldUseThemeColor ? { background: currentTheme.color } : undefined}
                        >
                            {confirmText}
                        </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default ConfirmationModal;
